import type { Adapter } from "./adapters";
import { BridgeError, type Envelope } from "./envelope";

export type Handler = (payload: unknown) => void;

export interface CallOptions {
  timeoutMs?: number;
}

export interface BridgeApi {
  on(event: string, handler: Handler): () => void;
}

export interface BridgeInstance {
  /** Web→host call expecting a response. Resolves/rejects on the matching response. */
  callNativeBridge<T = unknown>(event: string, payload?: unknown, opts?: CallOptions): Promise<T>;
  /** Subscribe to host-pushed events. Returns an unsubscribe function. */
  bridge: BridgeApi;
  /** Native→web entry point. Hosts deliver inbound envelopes here. */
  receive(raw: unknown): void;
}

const DEFAULT_TIMEOUT_MS = 10_000;

type Pending = { resolve: (v: unknown) => void; reject: (e: Error) => void };

/**
 * Build a bridge instance bound to one carrier `adapter`. Pure transport: request/response
 * correlation, timeouts, event dispatch. The adapter is injected so the core is testable
 * without any host globals; production wiring lives in `index.ts`.
 */
export function createBridge(adapter: Adapter | null): BridgeInstance {
  const handlers = new Map<string, Set<Handler>>();
  const pending = new Map<string, Pending>();

  function receive(raw: unknown): void {
    let env: Envelope;
    try {
      env = (typeof raw === "string" ? JSON.parse(raw) : raw) as Envelope;
    } catch (e) {
      console.error("[bridge] bad_payload: invalid JSON from host", { error: e, raw });
      return;
    }
    if (!env || typeof env !== "object" || env.v !== 1 || !("kind" in env)) {
      console.error("[bridge] bad_payload: invalid envelope shape from host", { env });
      return;
    }
    if (env.kind === "event") {
      handlers.get(env.event)?.forEach((h) => h(env.payload));
    } else if (env.kind === "response") {
      const p = pending.get(env.id);
      if (!p) return;
      pending.delete(env.id);
      if (env.ok) {
        p.resolve(env.result);
      } else {
        p.reject(new BridgeError(env.error));
      }
    }
  }

  function callNativeBridge<T = unknown>(
    event: string,
    payload?: unknown,
    opts: CallOptions = {},
  ): Promise<T> {
    if (!adapter) {
      // Standalone/dev: no host carrier. Reject promptly instead of timing out.
      return Promise.reject(new BridgeError({ code: "unsupported_event", message: event }));
    }
    const id = crypto.randomUUID();
    const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    return new Promise<T>((resolve, reject) => {
      pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      adapter.send(
        payload === undefined
          ? { v: 1, kind: "request", id, event }
          : { v: 1, kind: "request", id, event, payload },
      );
      setTimeout(() => {
        if (pending.delete(id)) {
          reject(new BridgeError({ code: "timeout", message: event }));
        }
      }, timeoutMs);
    });
  }

  const bridge: BridgeApi = {
    on(event, handler) {
      const set = handlers.get(event) ?? new Set<Handler>();
      set.add(handler);
      handlers.set(event, set);
      return () => {
        set.delete(handler);
      };
    },
  };

  return { callNativeBridge, bridge, receive };
}
